export enum EarlyRepaymentType {
  // biome-ignore lint/style/useNamingConvention: enum values match API contract
  FREE = "FREE",
  // biome-ignore lint/style/useNamingConvention: enum values match API contract
  FREE_AFTER_PERIOD = "FREE_AFTER_PERIOD",
  // biome-ignore lint/style/useNamingConvention: enum values match API contract
  FEE_APPLIES = "FEE_APPLIES",
  // biome-ignore lint/style/useNamingConvention: enum values match API contract
  FULL_INTEREST_AND_FEES = "FULL_INTEREST_AND_FEES",
}

export enum EarlyRepaymentUnit {
  // biome-ignore lint/style/useNamingConvention: enum values match API contract
  PERCENT = "PERCENT",
  // biome-ignore lint/style/useNamingConvention: enum values match API contract
  AMOUNT = "AMOUNT",
}

/**
 * The terms as one value object. This exact shape is the wire on every surface
 * the frontend talks to — customer GraphQL, admin CRM GraphQL (read and
 * mutation input) and the lender portal v2 REST, in both directions. The
 * portal's pydantic models carry `alias_generator=to_camel`, so the key is
 * `earlyRepayment` with `paidPeriodMonths` inside, and the REST payload is
 * posted verbatim — there is no camel/snake adapter anywhere in the frontend,
 * and none is needed. (lender_integration is snake_case `early_repayment`, but
 * that is the machine-to-machine partner API and no app here calls it.)
 *
 * An object rather than loose fields because the members are only meaningful
 * together — FREE forbids a unit, value or period; FEE_APPLIES takes a unit
 * and value or neither; FREE_AFTER_PERIOD requires all three. Loose fields
 * could express every illegal combination of those, which is what
 * `earlyRepaymentConsistency` exists to police.
 *
 * `comment` is the RENAMED legacy `earlyRepaymentFee` column, so it carries
 * migrated free text. `value` is a number on all four surfaces (Float, not
 * Decimal) — no per-surface conversion.
 *
 * `FEE_APPLIES` with a null `unit` and `value` is a REAL state, not a bug: it
 * means a fee applies whose size was never recorded, which is what the
 * backend's classifier returns for legacy prose it cannot parse. Render the
 * type and the comment; never substitute 0.
 */
export interface IEarlyRepayment {
  type: EarlyRepaymentType | null;
  unit: EarlyRepaymentUnit | null;
  value: number | null;
  paidPeriodMonths: number | null;
  comment: string | null;
}

/**
 * Carried by every bid type that has early-repayment terms — company loan and
 * real estate only. Non-null: core-service always constructs the object, and a
 * bid with no terms arrives as `{ type: FREE, … nulls }` rather than as null,
 * so readers never need an outer guard before reaching `type`.
 */
export interface IEarlyRepaymentFields {
  earlyRepayment: IEarlyRepayment;
}

/**
 * An empty-but-present object, for defaults and for seeding a new bid.
 *
 * Frozen because it is aliased: every `*BidDefaultValues` holds this same
 * reference, and a shallow `{ ...defaults }` (which is how a new bid is built)
 * copies the reference rather than the object. The flat fields this replaced
 * were primitives and could not be shared this way — an in-place write here
 * would have silently rewritten the default for every subsequent bid.
 */
export const FREE_EARLY_REPAYMENT: IEarlyRepayment = Object.freeze({
  type: EarlyRepaymentType.FREE,
  unit: null,
  value: null,
  paidPeriodMonths: null,
  comment: null,
});

/**
 * English names, shared by the picker and lender/admin read views (both
 * en-only) so a bid cannot be entered under one wording and read back under
 * another. Customer and partner translate the same four via `t()`.
 */
export const EARLY_REPAYMENT_TYPE_LABELS: Record<EarlyRepaymentType, string> = {
  [EarlyRepaymentType.FREE]: "Free - can be repaid at any time",
  [EarlyRepaymentType.FREE_AFTER_PERIOD]: "Free after a period",
  [EarlyRepaymentType.FEE_APPLIES]: "A fee applies",
  [EarlyRepaymentType.FULL_INTEREST_AND_FEES]: "All costs apply",
};

export type EarlyRepaymentSummary =
  | { kind: "free" }
  | {
      kind: "freeAfterPeriod";
      months: number;
      unit: EarlyRepaymentUnit;
      value: number;
    }
  | { kind: "feeApplies"; unit: EarlyRepaymentUnit; value: number }
  | { kind: "fullInterestAndFees" }
  // Type known, fee numbers missing (a migrated bid). Separate from
  // `unspecified` because collapsing the two threw the type away.
  | { kind: "typeOnly"; type: EarlyRepaymentType }
  // No type either, so there is nothing to name.
  | { kind: "unspecified" };

export interface IEarlyRepaymentDescriptor {
  summary: EarlyRepaymentSummary;
  comment: string | null;
}
